"use client";

import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getProjectGroupingKey } from "@/app/[locale]/[orgId]/[teamId]/(main)/files/utils";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useMe } from "@/hooks/useMe";
import { useOrganization } from "@/hooks/useOrganization";
import { useTeamsByOrganization } from "@/hooks/useTeamsByOrganization";
import { useTeamsContext } from "@/hooks/useTeamsContext";
import { buildOrgTeamPath, stripOrgTeamPrefix } from "@/lib/orgTeamPath";
import { persistTeamCookie } from "@/lib/teamCookie";
interface UseOrganizationTeamSwitcherReturn {
  teams: Array<{
    id: string;
    name: string;
  }>;
  currentTeamId?: string | null;
  isLoading: boolean;
  isUpdating: boolean;
  hasSingleTeam: boolean;
  hasMultipleOrganizations: boolean;
  currentOrganization: string | null;
  onTeamChange: (teamId: string) => Promise<void>;
}

// Hook to filter teams by the selected organization for the Team Switcher
export const useOrganizationTeamSwitcher = (): UseOrganizationTeamSwitcherReturn => {
  const {
    currentOrganization,
    currentOrganizationId,
    organizations
  } = useOrganization();
  const {
    data: teamsResponse,
    isLoading: teamsLoading
  } = useTeamsByOrganization();
  const {
    setCurrentTeam,
    isLoading: meLoading,
    isUpdating
  } = useMe();
  const {
    setClientTeamId,
    currentTeamId
  } = useTeamsContext();
  const pathname = usePathname();
  const urlOrgId = pathname.split("/")[2];
  const router = useRouter();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const {
    "projects-show-smart-folders-pd-6-467": smartFoldersEnabled
  } = useFeatureFlags();
  const hasMultipleOrganizations = organizations.length > 1;
  const teams = teamsResponse?.data || [];
  const hasSingleTeam = teams.length === 1;
  const displayTeams = useMemo(() => teams.map(team => ({
    id: team.id,
    name: team.attributes?.name ?? ""
  })).filter(team_0 => team_0.name), [teams]);
  const onTeamChange = useCallback(async (teamId: string) => {
    try {
      const selectedTeam = teams.find(team_1 => team_1.id === teamId);
      const scope = selectedTeam?.relationships?.scope?.data;
      const scopeId = Array.isArray(scope) ? scope[0]?.id : scope?.id;
      if (scopeId && currentOrganizationId) {
        const logicalPath = stripOrgTeamPrefix(pathnameRef.current);
        const isOrgSwitch = urlOrgId !== currentOrganizationId;
        const isOnProjectPage = logicalPath.match(/^\/files\/projects\/[^/]+$/);
        const isOnTeamDetailPage = logicalPath.match(/^\/settings\/teams\/[^/]+/);
        let targetPath = logicalPath;
        if (isOnProjectPage) {
          // Read the *target* team's persisted grouping (not the outgoing
          // team's via useProjectsHomeHref) so we don't land on /grouped-projects
          // for a team that doesn't have that preference.
          const targetGroupBy = localStorage.getItem(getProjectGroupingKey(currentOrganizationId, teamId));
          targetPath = smartFoldersEnabled && targetGroupBy ? `/grouped-projects?groupBy=${encodeURIComponent(targetGroupBy)}` : "/files";
        }
        // Exclude team switching from kicking user back to the teams list
        else if (isOnTeamDetailPage && isOrgSwitch) targetPath = "/settings/teams";
        const newPath = buildOrgTeamPath(targetPath, {
          orgId: currentOrganizationId,
          teamId
        });
        const separator = newPath.includes("?") ? "&" : "?";
        const newUrl = `${newPath}${separator}scopeId=${scopeId}&scopeType=TEAM`;
        if (isOrgSwitch) {
          await setCurrentTeam(teamId);
          if (!isMountedRef.current) return;
          router.push(newUrl);
        } else {
          setClientTeamId(teamId);
          await setCurrentTeam(teamId);
          if (!isMountedRef.current) return;
          router.push(newUrl);
          // allows middleware to read the current team on next server request (for proxy headers, redirects, etc.)
          await persistTeamCookie(teamId);
        }
      }
    } catch (error) {
      console.error("Team change failed, error already handled:", error);
    }
  }, [setCurrentTeam, setClientTeamId, teams, router, currentOrganizationId, urlOrgId, smartFoldersEnabled]);
  const currentTeamExists = currentTeamId ? teams.some(team_2 => team_2.id === currentTeamId) : false;
  const isLoading = teamsLoading || meLoading;

  // Track if we're in the middle of an org-triggered team change to avoid duplicate calls
  const isAutoSwitchingTeam = useRef(false);

  // Track org changes so we only auto-select a team when the user explicitly switches orgs.
  // Without this, a team that exists but isn't in the (possibly truncated) org-filtered list
  // would be wrongly overridden on every render.
  // IMPORTANT: Use currentOrganizationId (from localStorage, updates immediately) instead of
  // currentOrganization?.id (from API fetch, goes null during loading and breaks detection).
  const prevOrgIdRef = useRef(currentOrganizationId);
  const [orgChanged, setOrgChanged] = useState(false);
  useEffect(() => {
    const prevOrgId = prevOrgIdRef.current;
    if (prevOrgId && currentOrganizationId && prevOrgId !== currentOrganizationId) {
      setOrgChanged(true);
    }
    prevOrgIdRef.current = currentOrganizationId;
  }, [currentOrganizationId]);
  const effectiveCurrentTeamId = useMemo(() => {
    if (currentTeamExists) {
      return currentTeamId;
    }
    if (teams.length > 0) {
      return teams[0].id;
    }
    return null;
  }, [currentTeamExists, currentTeamId, teams]);
  useEffect(() => {
    if (isLoading || isAutoSwitchingTeam.current) {
      return;
    }

    // Two cases where we auto-select a team:
    // 1. No team set at all (first login) — pick the first available team
    // 2. Org changed and current team isn't in the new org — pick the new org's first team
    //
    // We do NOT auto-select when a team is set but simply missing from a
    // truncated list (the team may be beyond the page size).
    const needsInitialTeam = !currentTeamId && teams.length > 0;
    const needsTeamAfterOrgChange = orgChanged && !currentTeamExists && teams.length > 0;
    if (needsInitialTeam || needsTeamAfterOrgChange) {
      const firstTeamId = teams[0].id;
      if (currentTeamId !== firstTeamId) {
        isAutoSwitchingTeam.current = true;
        setOrgChanged(false);
        void onTeamChange(firstTeamId).finally(() => {
          isAutoSwitchingTeam.current = false;
        });
      } else {
        setOrgChanged(false);
      }
    }
  }, [orgChanged, currentTeamExists, currentTeamId, teams, isLoading, onTeamChange]);
  return {
    teams: displayTeams,
    currentTeamId: effectiveCurrentTeamId,
    isLoading,
    isUpdating,
    hasSingleTeam,
    hasMultipleOrganizations,
    currentOrganization: currentOrganization?.attributes?.name || null,
    onTeamChange
  };
};